import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import RepDashboard from './RepDashboard';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Phone, TrendingUp, Search, Plus, Archive, User, Download } from 'lucide-react';

import KpiGrid from '../components/dashboard/KpiGrid';
import GradeDistribution from '../components/dashboard/GradeDistribution';
import SkillTrendsView from '../components/dashboard/SkillTrendsView';
import DealCard from '../components/dashboard/DealCard';
import CallCard from '../components/dashboard/CallCard';
import ArchivedCallCard from '../components/dashboard/ArchivedCallCard';
import TeamMemberCard from '../components/dashboard/TeamMemberCard';
import ExportModal from '../components/dashboard/ExportModal';
import UsageWidget from '../components/dashboard/UsageWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { isManager } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── Data ───
  const [calls, setCalls] = useState([]);
  const [deals, setDeals] = useState([]);
  const [archivedCalls, setArchivedCalls] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [teamStats, setTeamStats] = useState([]);
  const [myPerformance, setMyPerformance] = useState(null);
  const [taskSummary, setTaskSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── Filters / UI ───
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [classificationFilter, setClassificationFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [activeTab, setActiveTab] = useState('deals');
  // Persisted to URL (?period=weekly|monthly|all) so navigating between
  // pages preserves the choice. Default = monthly.
  const validPeriods = ['weekly', 'monthly', 'all'];
  const initialPeriod = validPeriods.includes(searchParams.get('period'))
    ? searchParams.get('period')
    : 'monthly';
  const [timePeriod, setTimePeriodState] = useState(initialPeriod);
  const setTimePeriod = (val) => {
    setTimePeriodState(val);
    const next = new URLSearchParams(searchParams);
    next.set('period', val);
    setSearchParams(next, { replace: true });
  };

  // ─── Modals & per-deal state ───
  const [showExport, setShowExport] = useState(false);
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [dealTaskRecs, setDealTaskRecs] = useState({});
  const [creatingDealTask, setCreatingDealTask] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, sortBy, timePeriod]);

  const fetchData = async () => {
    try {
      let callsUrl = `${API_URL}/api/calls?archived=false&sort_by=${sortBy}`;
      if (dateFilter !== 'all') callsUrl += `&date_filter=${dateFilter}`;

      // Map the shared timePeriod to the /performance/me short-form param.
      const perfPeriod = timePeriod === 'weekly'
        ? 'week'
        : timePeriod === 'all'
          ? 'all'
          : 'month';

      const [callsRes, metricsRes, perfRes, dealsRes, taskSumRes] = await Promise.all([
        fetch(callsUrl, { credentials: 'include' }),
        fetch(`${API_URL}/api/dashboard/metrics?period=${timePeriod}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/performance/me?period=${perfPeriod}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/dashboard/deals?period=${timePeriod}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/tasks/summary`, { credentials: 'include' })
      ]);

      if (callsRes.ok) setCalls(await callsRes.json());
      if (metricsRes.ok) setMetrics(await metricsRes.json());
      if (perfRes.ok) setMyPerformance(await perfRes.json());
      if (dealsRes.ok) setDeals(await dealsRes.json());
      if (taskSumRes.ok) setTaskSummary(await taskSumRes.json());

      const archivedRes = await fetch(`${API_URL}/api/calls?archived=true`, { credentials: 'include' });
      if (archivedRes.ok) setArchivedCalls(await archivedRes.json());

      if (isManager) {
        const teamRes = await fetch(`${API_URL}/api/dashboard/team`, { credentials: 'include' });
        if (teamRes.ok) setTeamStats(await teamRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDealOutcome = async (company, outcome) => {
    try {
      const res = await fetch(`${API_URL}/api/dashboard/deals/outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ company, outcome })
      });
      if (res.ok) {
        setDeals(prev => prev.map(d => d.company === company ? { ...d, outcome } : d));
        toast.success(`${company} marked as ${outcome}`);
      }
    } catch {
      toast.error('Failed to update deal status');
    }
  };

  const fetchDealTaskRecs = async (deal) => {
    if (dealTaskRecs[deal.company]) return;
    const latestCall = deal.calls?.find(c => c.status === 'completed');
    if (!latestCall) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/recommend/${latestCall.call_id}`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const recs = await res.json();
        setDealTaskRecs(prev => ({ ...prev, [deal.company]: recs }));
      }
    } catch (e) { console.error(e); }
  };

  const handleToggleDeal = (deal) => {
    const next = expandedDeal === deal.company ? null : deal.company;
    setExpandedDeal(next);
    if (next) fetchDealTaskRecs(deal);
  };

  const acceptDealTask = async (rec, company) => {
    setCreatingDealTask(rec.title);
    try {
      const res = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: rec.title, company: rec.company, call_id: rec.call_id, due_date: rec.due_date, priority: rec.priority })
      });
      if (res.ok) {
        toast.success('Task created');
        setDealTaskRecs(prev => ({ ...prev, [company]: (prev[company] || []).filter(r => r.title !== rec.title) }));
      }
    } catch {
      toast.error('Failed');
    } finally {
      setCreatingDealTask(null);
    }
  };

  // ─── Filtering ───
  const filteredCalls = calls.filter(call => {
    const matchesSearch = call.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (call.company_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || call.status === statusFilter;
    const matchesClassification = classificationFilter === 'all' || call.grade === classificationFilter || call.classification === classificationFilter;
    return matchesSearch && matchesStatus && matchesClassification;
  });

  const filteredDeals = deals.filter(d => {
    const matchSearch = d.company.toLowerCase().includes(searchQuery.toLowerCase());
    const matchGrade = classificationFilter === 'all' || d.grade === classificationFilter;
    return matchSearch && matchGrade;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="cyber-spinner"></div>
        </div>
      </Layout>
    );
  }

  // Rep view gets dedicated dashboard
  if (!isManager) return <RepDashboard />;

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Team Dashboard</h1>
            <p className="text-muted-foreground mt-1">Monitor team performance and call analytics</p>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={timePeriod} onValueChange={setTimePeriod}>
              <SelectTrigger className="w-28 h-9 text-xs bg-card border-border" data-testid="time-period-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setShowExport(true)} className="border-border" data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button
              onClick={() => navigate('/upload')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow"
              data-testid="new-call-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Call
            </Button>
          </div>
        </div>

        {/* KPI strip */}
        <KpiGrid metrics={metrics} taskSummary={taskSummary} onTasksClick={() => navigate('/tasks')} />

        {/* Plan usage meters */}
        <UsageWidget />

        {/* Grade distribution */}
        <GradeDistribution metrics={metrics} />

        {/* Skill trends — always visible */}
        {myPerformance && <SkillTrendsView performance={myPerformance} period={timePeriod} />}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="deals" className="data-[state=active]:bg-primary/10">
              <TrendingUp className="w-4 h-4 mr-2" />
              Deals
            </TabsTrigger>
            <TabsTrigger value="calls" className="data-[state=active]:bg-primary/10">
              <Phone className="w-4 h-4 mr-2" />
              Calls
            </TabsTrigger>
            <TabsTrigger value="archived" className="data-[state=active]:bg-primary/10">
              <Archive className="w-4 h-4 mr-2" />
              Archived ({archivedCalls.length})
            </TabsTrigger>
            <TabsTrigger value="team" className="data-[state=active]:bg-primary/10">
              <User className="w-4 h-4 mr-2" />
              Team
            </TabsTrigger>
          </TabsList>

          {/* ─── Deals Tab ─── */}
          <TabsContent value="deals" className="mt-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search deals..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-card border-border"
                  data-testid="deal-search-input"
                />
              </div>
              <Select value={classificationFilter} onValueChange={setClassificationFilter}>
                <SelectTrigger className="w-full sm:w-36 bg-card border-border" data-testid="deal-grade-filter">
                  <SelectValue placeholder="Grade" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Grades</SelectItem>
                  <SelectItem value="Hot Deal">Hot Deal</SelectItem>
                  <SelectItem value="SQL">SQL</SelectItem>
                  <SelectItem value="Warm Lead">Warm Lead</SelectItem>
                  <SelectItem value="Cold Lead">Cold Lead</SelectItem>
                  <SelectItem value="Disqualified">Disqualified</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {filteredDeals.length === 0 ? (
              <EmptyState
                icon={<TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />}
                title="No deals yet"
                desc="Upload sales calls to start tracking deal progress"
                onAction={() => navigate('/upload')}
              />
            ) : (
              <div className="grid gap-3">
                {filteredDeals.map((deal) => (
                  <DealCard
                    key={deal.company}
                    deal={deal}
                    isExpanded={expandedDeal === deal.company}
                    onToggle={handleToggleDeal}
                    onOutcome={handleDealOutcome}
                    taskRecs={dealTaskRecs[deal.company]}
                    onAcceptTask={acceptDealTask}
                    creatingTaskTitle={creatingDealTask}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* ─── Calls Tab ─── */}
          <TabsContent value="calls" className="mt-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search calls or companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-card border-border"
                  data-testid="search-input"
                />
              </div>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-full sm:w-32 bg-card border-border" data-testid="date-filter">
                  <SelectValue placeholder="Date" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-36 bg-card border-border" data-testid="sort-filter">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="deal_score">Highest Win %</SelectItem>
                  <SelectItem value="risk">Highest Risk</SelectItem>
                </SelectContent>
              </Select>
              <Select value={classificationFilter} onValueChange={setClassificationFilter}>
                <SelectTrigger className="w-full sm:w-32 bg-card border-border" data-testid="classification-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Grades</SelectItem>
                  <SelectItem value="Hot Deal">Hot Deal</SelectItem>
                  <SelectItem value="SQL">SQL</SelectItem>
                  <SelectItem value="Warm Lead">Warm Lead</SelectItem>
                  <SelectItem value="Cold Lead">Cold Lead</SelectItem>
                  <SelectItem value="Disqualified">Disqualified</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {filteredCalls.length === 0 ? (
              <EmptyState
                icon={<Phone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />}
                title="No calls yet"
                desc="Upload your first sales call to get started"
                onAction={() => navigate('/upload')}
              />
            ) : (
              <div className="grid gap-4">
                {filteredCalls.map((call) => <CallCard key={call.call_id} call={call} />)}
              </div>
            )}
          </TabsContent>

          {/* ─── Archived Tab ─── */}
          <TabsContent value="archived" className="mt-6">
            {archivedCalls.length === 0 ? (
              <Card className="bg-card border-border/50">
                <CardContent className="p-12 text-center">
                  <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">No archived calls</h3>
                  <p className="text-muted-foreground">Archive completed calls to organize your history</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {archivedCalls.map((call) => <ArchivedCallCard key={call.call_id} call={call} />)}
              </div>
            )}
          </TabsContent>

          {/* ─── Team Tab ─── */}
          <TabsContent value="team" className="mt-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {teamStats.map((member) => <TeamMemberCard key={member.user_id} member={member} onUpdate={fetchData} />)}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {showExport && <ExportModal period={timePeriod} onClose={() => setShowExport(false)} />}
    </Layout>
  );
}

// ─── Local empty-state helper used by Deals/Calls tabs ───
function EmptyState({ icon, title, desc, onAction }) {
  return (
    <Card className="bg-card border-border/50">
      <CardContent className="p-12 text-center">
        {icon}
        <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{title}</h3>
        <p className="text-muted-foreground mb-4">{desc}</p>
        <Button onClick={onAction} className="bg-primary text-primary-foreground hover:bg-primary/90">
          Upload Call
        </Button>
      </CardContent>
    </Card>
  );
}
